import { useEffect, useRef } from 'react';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { LoadingSpinner } from '../shared/LoadingSpinner';

type ConfirmColor = 'green' | 'yellow' | 'red';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmLabel: string;
  confirmColor?: ConfirmColor;
  isLoading?: boolean;
  icon?: 'warning' | 'success' | 'shield';
}

const iconMap = {
  warning: ExclamationTriangleIcon,
  success: CheckCircleIcon,
  shield: ShieldCheckIcon,
};

const iconColorMap: Record<ConfirmColor, string> = {
  green: 'bg-green-100 text-green-600',
  yellow: 'bg-yellow-100 text-yellow-600',
  red: 'bg-red-100 text-red-600',
};

const buttonColorMap: Record<ConfirmColor, string> = {
  green: 'bg-green-600 hover:bg-green-700 focus:ring-green-500 text-white',
  yellow: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500 text-white',
  red: 'bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white',
};

export function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  confirmColor = 'green',
  isLoading = false,
  icon = 'warning',
}: ConfirmationModalProps) {
  const IconComponent = iconMap[icon];
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  // Auto-focus confirm button when modal opens
  useEffect(() => {
    if (isOpen) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        confirmButtonRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle Escape key to close
  useEffect(() => {
    if (!isOpen || isLoading) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    // Use capture phase to intercept before parent Dialog
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirmation-title"
      aria-describedby="confirmation-message"
      onClick={(e) => {
        // Close when clicking the backdrop (not the panel)
        if (e.target === e.currentTarget && !isLoading) {
          onClose();
        }
      }}
    >
      {/* Backdrop — pointer-events-none so clicks pass through to the outer div's onClick handler */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm pointer-events-none" />

      {/* Panel */}
      <div className="relative w-full max-w-md transform">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-6">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className={`rounded-full p-3 ${iconColorMap[confirmColor]}`}>
                <IconComponent className="h-8 w-8" />
              </div>
            </div>

            {/* Title */}
            <h3
              id="confirmation-title"
              className="text-lg font-semibold text-gray-900 text-center mb-2"
            >
              {title}
            </h3>

            {/* Message */}
            <p
              id="confirmation-message"
              className="text-sm text-gray-600 text-center leading-relaxed"
            >
              {message}
            </p>
          </div>

          {/* Actions */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="inline-flex justify-center rounded-lg px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              ref={confirmButtonRef}
              type="button"
              onClick={onConfirm}
              disabled={isLoading}
              className={`inline-flex justify-center items-center rounded-lg px-5 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors disabled:opacity-70 ${buttonColorMap[confirmColor]}`}
            >
              {isLoading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Processing...
                </>
              ) : (
                confirmLabel
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
